import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { MapPin, Loader2, RefreshCw, Send, Briefcase, ExternalLink, FileText } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import OrderPhotosUploader from "@/components/OrderPhotosUploader";

const STATUSES = ["ordered", "enroute", "on_site", "in_progress", "completed"];
const STATUS_LABEL = { ordered: "Ordered", enroute: "En route", on_site: "On site", in_progress: "In progress", completed: "Completed" };
const QUOTE_STATUS_LABEL = { reviewing: "Reviewing", enroute: "En route", on_site: "On site", quoted: "Quoted", sold: "Sold" };
const fmt = (c) => `$${(c / 100).toFixed(2)}`;

function timeAgo(iso) {
  if (!iso) return "—";
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.round(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
}

export default function MyJobs() {
  const { user } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [assignedQuotes, setAssignedQuotes] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const [locating, setLocating] = useState(null);
  const isAdminOrOwner = user?.role === "admin" || user?.role === "owner";

  const load = useCallback(async () => {
    try {
      const r = await api.get("/me/orders/assigned");
      setJobs(r.data);
      if (isAdminOrOwner) {
        const q = await api.get("/me/quotes/assigned").catch(() => ({ data: [] }));
        setAssignedQuotes(q.data);
      }
    }
    catch (e) { toast.error(e.response?.data?.detail || "Failed to load jobs"); }
  }, [isAdminOrOwner]);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    setBusyId(id);
    try {
      await api.patch(`/orders/${id}/status`, { status });
      setJobs((arr) => arr.map((j) => (j.id === id ? { ...j, status } : j)));
      toast.success(`Marked ${STATUS_LABEL[status]}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
    finally { setBusyId(null); }
  };

  const sendLocation = (id) => {
    if (!navigator.geolocation) { toast.error("Geolocation not supported on this device"); return; }
    setLocating(id);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const r = await api.patch(`/orders/${id}/location`, { lat: pos.coords.latitude, lng: pos.coords.longitude });
          setJobs((arr) => arr.map((j) => (j.id === id ? { ...j, tech_location: r.data.tech_location } : j)));
          toast.success("Location shared");
        } catch (e) { toast.error(e.response?.data?.detail || "Location update failed"); }
        finally { setLocating(null); }
      },
      (err) => {
        toast.error(err.message || "Could not get location");
        setLocating(null);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 30000 },
    );
  };

  return (
    <div>
      <div className="flex items-baseline justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-bold text-slate-900 inline-flex items-center gap-2"><Briefcase className="h-6 w-6 text-cyan-700" /> My jobs</h1>
          <p className="mt-1 text-sm text-slate-500">{jobs.length} assigned to you</p>
        </div>
        <Button variant="outline" size="sm" onClick={load} data-testid="my-jobs-refresh-button"><RefreshCw className="h-4 w-4" /> Refresh</Button>
      </div>

      {jobs.length === 0 && (
        <div className="mt-8 rounded-2xl border border-dashed border-slate-300 p-12 text-center text-slate-500">
          No orders assigned yet. {user?.role === "technician" ? "Check back after the office assigns one to you." : "Orders the office assigns to you will appear here."}
        </div>
      )}

      {isAdminOrOwner && assignedQuotes.length > 0 && (
        <section className="mt-6">
          <h2 className="font-display text-xl font-bold text-slate-900 inline-flex items-center gap-2"><FileText className="h-4 w-4 text-cyan-700" /> Assigned quotes ({assignedQuotes.length})</h2>
          <ul className="mt-3 grid gap-3 md:grid-cols-2">
            {assignedQuotes.map((q) => (
              <li key={q.id} className="dash-surface p-4" data-testid={`my-quote-${q.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="font-medium text-slate-900">{q.name}</div>
                    <div className="text-xs text-slate-500">{q.project_type}</div>
                  </div>
                  <span className="rounded-full bg-cyan-100 px-2 py-0.5 text-[10px] font-semibold uppercase text-cyan-800">{QUOTE_STATUS_LABEL[q.status] || q.status}</span>
                </div>
                <div className="mt-2 text-xs text-slate-500 truncate">{q.address}</div>
                <Link to="/dashboard/quotes" className="mt-2 inline-block text-xs font-medium text-cyan-700 hover:underline">Open in Quotes →</Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      {jobs.length > 0 && isAdminOrOwner && <h2 className="mt-8 font-display text-xl font-bold text-slate-900 inline-flex items-center gap-2"><Briefcase className="h-4 w-4 text-cyan-700" /> Assigned orders ({jobs.length})</h2>}

      <ul className="mt-6 space-y-4">
        {jobs.map((j) => {
          const loc = j.tech_location;
          const mapQ = loc ? `${loc.lat},${loc.lng}` : j.shipping_address;
          return (
            <li key={j.id} className="dash-surface p-5 md:p-6" data-testid={`my-job-${j.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="font-display text-lg font-bold text-slate-900">{j.customer_name}</div>
                  <div className="mt-1 flex flex-wrap gap-3 text-sm text-slate-600">
                    <span>{j.customer_phone}</span>
                    <span>{j.customer_email}</span>
                  </div>
                  <div className="mt-2 inline-flex items-center gap-1 text-sm text-slate-700"><MapPin className="h-3.5 w-3.5 text-cyan-700" /> {j.shipping_address}</div>
                </div>
                <Select value={j.status} onValueChange={(v) => setStatus(j.id, v)} disabled={busyId === j.id}>
                  <SelectTrigger className="w-44" data-testid={`my-job-status-${j.id}`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {STATUSES.map((s) => <SelectItem key={s} value={s}>{STATUS_LABEL[s]}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              <div className="mt-4 grid gap-4 md:grid-cols-2">
                <div>
                  <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">Items / scope</div>
                  <ul className="mt-2 space-y-1 text-sm text-slate-700">
                    {(j.items || []).map((it, i) => (
                      <li key={it.product_id || `${it.name}-${i}`} className="flex justify-between gap-3">
                        <span>{it.quantity}× {it.name}</span>
                        <span className="text-slate-500">{fmt(it.price_cents * it.quantity)}</span>
                      </li>
                    ))}
                  </ul>
                  {j.notes && <p className="mt-3 rounded-md bg-slate-50 p-3 text-sm text-slate-700">{j.notes}</p>}
                </div>
                <div>
                  <div className="text-xs font-semibold uppercase tracking-widest text-slate-500 flex items-center justify-between">
                    <span>Map</span>
                    <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(mapQ || "")}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-cyan-700 normal-case tracking-normal hover:underline" data-testid={`my-job-open-map-${j.id}`}>
                      <ExternalLink className="h-3 w-3" /> Open
                    </a>
                  </div>
                  <div className="mt-2 overflow-hidden rounded-md border border-slate-200">
                    <iframe
                      title={`Map for ${j.id}`}
                      src={`https://maps.google.com/maps?q=${encodeURIComponent(mapQ || "")}&z=14&output=embed`}
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      className="block h-44 w-full"
                    />
                  </div>
                  {loc && (
                    <div className="mt-2 text-[11px] text-slate-500">Last GPS update: {timeAgo(loc.updated_at)} ({loc.lat.toFixed(4)}, {loc.lng.toFixed(4)})</div>
                  )}
                </div>
              </div>

              <div className="mt-4 flex flex-wrap gap-2">
                <Button onClick={() => sendLocation(j.id)} disabled={locating === j.id} data-testid={`my-job-send-location-${j.id}`}>
                  {locating === j.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  Send my location
                </Button>
                <a href={`tel:${j.customer_phone || ""}`} className="inline-flex items-center gap-2 rounded-md border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 hover:border-cyan-300 hover:text-cyan-700" data-testid={`my-job-call-${j.id}`}>
                  Call customer
                </a>
              </div>

              <div className="mt-4">
                <OrderPhotosUploader
                  orderId={j.id}
                  photos={j.completion_photos || []}
                  onChange={(next) => setJobs((arr) => arr.map((x) => (x.id === j.id ? { ...x, completion_photos: next } : x)))}
                  canRemovePhoto={(p) => isAdminOrOwner || p.uploaded_by_user_id === user?.user_id}
                />
              </div>
            </li>
          );
        })}
      </ul>

      <p className="mt-6 text-xs text-slate-400">
        Tip: Tap "Send my location" once when you're en route and again on site so the office sees your live position. <Link to="/dashboard" className="text-cyan-700 hover:underline">Back to overview</Link>
      </p>
    </div>
  );
}
